/**
 * Виплата нарахувань — список (канон Норми #29, 2026-08-23): InfiniteDocGrid,
 * серверні фільтри орг/вид/період + сортування, колонки «Вкладення»/«Підпис».
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { ColDef } from 'ag-grid-community';
import { payrollPayments, type PayrollPaymentListItem } from '@/api/payrolls';
import { orgs } from '@/api/catalogs';
import { useOrgFilter } from '@/store/defaults';
import ImportedBadge from '@/shared/components/ImportedBadge';
import InfiniteDocGrid, { TitleTotal } from '@/shared/components/InfiniteDocGrid';
import PeriodFilter, { useHrPeriodFilter, MONTHS_UK } from '@/shared/components/PeriodFilter';
import { useInfiniteGridDatasource } from '@/hooks/useInfiniteGridDatasource';
import { useSignControlInfinite } from '@/shared/hooks/useSignControl';

const fmt2 = (v: string | number) =>
  Number(v).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const METHOD_LABEL: Record<string, string> = { CASH: 'Готівка', BANK: 'Банк' };
const KIND_LABEL: Record<string, string> = {
  MAIN: 'ЗП основна', SALARY: 'ЗП основна', ADVANCE: 'Аванс', VACATION: 'Відпустка',
  COMPENSATION: 'Компенсація відпустки',
  SICK_ORG: 'Лікарняний (орг)', SICK_PFU: 'Лікарняний (ПФУ)', SICK: 'Лікарняні',
  CIVIL: 'Виплата за ЦПУ',
};

export default function PayrollPaymentsPage() {
  const navigate = useNavigate();
  const [orgId, setOrgId] = useOrgFilter();
  const [kind, setKind] = useState('');
  const per = useHrPeriodFilter();

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const orgName = useMemo(() => {
    const m = new Map<string, string>();
    orgList.data?.items.forEach((o) => m.set(o.id, o.name));
    return m;
  }, [orgList.data]);

  const { datasource, total } = useInfiniteGridDatasource<PayrollPaymentListItem>(
    (limit, offset, sort) => payrollPayments.list({
      organization_id: orgId || undefined,
      payout_kind: kind || undefined,
      ...per.params,
      sort_by: sort?.sort_by, sort_dir: sort?.sort_dir,
      limit, offset,
    }),
    [orgId, kind, ...per.key],
    { attachmentsType: 'doc.payroll_payments' },
  );
  const sc = useSignControlInfinite<PayrollPaymentListItem>('doc.payroll_payments', orgId);

  const cols = useMemo<ColDef<PayrollPaymentListItem>[]>(() => [
    {
      headerName: '', width: 44, sortable: false,
      cellRenderer: (p: { data?: PayrollPaymentListItem }) =>
        <ImportedBadge source={p.data?.external_source} />,
    },
    { field: 'document_number', headerName: '№', width: 140 },
    {
      field: 'organization_id', headerName: 'Організація', width: 190,
      valueFormatter: (p) => p.value ? (orgName.get(p.value) ?? '—') : '',
    },
    {
      field: 'document_date', headerName: 'Дата', width: 150,
      valueFormatter: (p) => p.value ? new Date(p.value).toLocaleString('uk-UA') : '',
    },
    {
      colId: 'period', headerName: 'Період', width: 130,
      valueGetter: (p) => p.data?.period_month
        ? `${MONTHS_UK[p.data.period_month]} ${p.data.period_year}` : (p.data ? '—' : ''),
    },
    {
      field: 'payout_kind', headerName: 'Вид виплати', width: 150,
      valueFormatter: (p) => p.data ? (KIND_LABEL[p.value ?? 'MAIN'] ?? (p.value ?? '—')) : '',
    },
    {
      field: 'payment_method', headerName: 'Метод', width: 150,
      valueGetter: (p) => {
        if (!p.data) return '';
        const ms = p.data.methods?.length ? p.data.methods : [p.data.payment_method];
        return ms.map((m) => METHOD_LABEL[m] ?? m).join(' + ');
      },
    },
    {
      field: 'total_amount', headerName: 'Сума', width: 140,
      cellStyle: { textAlign: 'right' },
      valueFormatter: (p) => p.value != null ? fmt2(p.value) : '',
    },
    {
      colId: '_att', headerName: 'Вкладення', width: 110, sortable: false,
      cellStyle: { textAlign: 'center' },
      cellRenderer: (p: { data?: { _att?: boolean } }) => p.data?._att ? '✓' : '',
    },
    {
      field: 'posted', headerName: 'Проведено', width: 110,
      cellStyle: { textAlign: 'center' },
      cellRenderer: (p: { data?: { posted?: boolean } }) =>
        p.data?.posted ? <span style={{ color: 'var(--badge-green-fg)', fontWeight: 600 }}>✓</span> : '',
    },
  ], [orgName]);

  return (
    <div className="card">
      <div className="toolbar with-filters">
        <h2 style={{ margin: 0 }}>Виплата нарахувань<TitleTotal total={total} /></h2>
        <button className="primary" onClick={() => navigate('/payroll-payments/new')}>
          + Нова виплата
        </button>
      </div>
      <div className="filters" style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', margin: '0 0 0.6rem', alignItems: 'center' }}>
        <select value={orgId} onChange={(e) => setOrgId(e.target.value)} style={{ maxWidth: 260 }}>
          <option value="">Усі організації</option>
          {orgList.data?.items.map((o) => <option key={o.id} value={o.id}>{o.name} — {o.code}</option>)}
        </select>
        <select value={kind} onChange={(e) => setKind(e.target.value)} style={{ width: 190 }}>
          <option value="">усі види виплат</option>
          <option value="MAIN">ЗП основна</option>
          <option value="ADVANCE">Аванс</option>
          <option value="VACATION">Відпустка</option>
          <option value="COMPENSATION">Компенсація відпустки</option>
          <option value="SICK_ORG">Лікарняний (орг)</option>
          <option value="SICK_PFU">Лікарняний (ПФУ)</option>
          <option value="CIVIL">Виплата за ЦПУ</option>
        </select>
        <PeriodFilter {...per.bind} />
      </div>
      <InfiniteDocGrid<PayrollPaymentListItem>
        deleteRow={{ remove: (id) => payrollPayments.remove(id) }}
        datasource={sc.wrap(datasource)}
        columnDefs={[...cols, ...sc.column]}
        storageKey="payroll-payments"
        onRowDoubleClicked={(r) => navigate(`/payroll-payments/${r.id}`)}
        getRowStyle={(p) => p.data?.deletion_mark ? { opacity: 0.4 } : sc.getRowStyle(p)}
      />
    </div>
  );
}
